import { useInfiniteQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { useTranslationsContext } from '@omnea-app/fe-i18n/TranslationsContext'
import { throwIfApiError } from '@omnea-app/packages/universal/error-handling/throwifApiError'
import { RequestTopLevelFilterKey } from '@omnea-app/requests/requestsFrontendClient'
import { RequestsHttpClient } from '@omnea-app/requests/requestsHttpClient'
import { buildRequestsV2RequestBody, transformV2Response } from './useRequestsDataSource'

export const REQUESTS_MOBILE_INFINITE_QUERY_KEY = 'requests-mobile-infinite'

const ITEMS_PER_PAGE = 5
const REQUESTS_V2_ENDPOINT = '/paginated-v2' as const

type UseRequestsMobileInfiniteQueryOptions = {
  userId: string
  topLevelFilter: RequestTopLevelFilterKey
  quickSearch: string
}

export const useRequestsMobileInfiniteQuery = (
  getToken: () => Promise<string>,
  { userId, topLevelFilter, quickSearch }: UseRequestsMobileInfiniteQueryOptions
) => {
  const { locale } = useTranslationsContext()

  const trimmedSearch = quickSearch.trim()

  // userId is keyed for the same reason as the data-source key: the row transform derives per-user flags.
  const query = useInfiniteQuery({
    queryKey: [REQUESTS_MOBILE_INFINITE_QUERY_KEY, topLevelFilter, trimmedSearch, userId, locale],
    initialPageParam: 0,
    queryFn: async ({ pageParam }) => {
      const client = new RequestsHttpClient({ authorization: await getToken() })
      const { data, error } = await client.POST(REQUESTS_V2_ENDPOINT, {
        body: buildRequestsV2RequestBody({
          paginationModel: { page: pageParam, pageSize: ITEMS_PER_PAGE },
          filterModel: trimmedSearch ? { items: [], quickFilterValues: [trimmedSearch] } : undefined,
          sortModel: [{ field: 'createdAt', sort: 'desc' }],
          topLevelFilter,
          locale,
        }),
      })
      throwIfApiError(error)
      if (!data) throw new Error(`No data returned from ${REQUESTS_V2_ENDPOINT}`)
      return transformV2Response(data, userId)
    },
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.rows.length < ITEMS_PER_PAGE) return undefined
      const loaded = allPages.reduce((sum, page) => sum + page.rows.length, 0)
      if (!lastPage.isApprox && loaded >= lastPage.rowCount) return undefined
      return allPages.length
    },
    staleTime: 300_000,
    gcTime: 600_000,
  })

  const rows = useMemo(() => query.data?.pages.flatMap((page) => page.rows) ?? [], [query.data])

  return {
    rows,
    rowCount: query.data?.pages[0]?.rowCount ?? 0,
    isLoading: query.isLoading,
    isError: query.isError,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    totalCountIsApprox: query.data?.pages[0]?.isApprox ?? false,
  }
}
